import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
	on,
	off,
	getWindowHeight,
	getBoundingClientRect,
} from '@sparefoot/react-core/utils/dom';
import { withShowHide } from '@sparefoot/react-core/decorators';
// import differenceInCalendarDays from 'date-fns/difference_in_calendar_days';
import {
	compose,
	defaultProps,
	withState,
	withHandlers,
	lifecycle,
	withPropsOnChange,
} from 'recompose';
import CSSTransition from 'react-transition-group/CSSTransition';
import { Calendar } from 'components/core/Calendar';
import { Input } from 'components/core/Input';

import './Datepicker.scss';

export const enhance = compose(
	defaultProps({ today: new Date() }),
	withShowHide('calendar'),
	withState('instance', 'setInstance', null),
	withPropsOnChange(['calendarShowing', 'instance'], ({ instance }) => {
		if (!instance) {
			return null;
		}
		const rect = getBoundingClientRect(instance);
		const above = rect.bottom + 250 > getWindowHeight();
		return {
			position: above ? 'above' : 'below',
		};
	}),
	withHandlers({
		getRef: (props) => props.setInstance,
		handleDateClick: (props) => (date) => {
			props.onHideCalendar();
			props.onChange({
				target: {
					name: props.name,
					value: date,
				},
			});

			if (props.onDateClick) {
				props.onDateClick(date);
			}
		},
		handlePageClick:
			({ instance, onHideCalendar }) =>
			(event) => {
				if (!instance || !instance.contains(event.target)) {
					onHideCalendar();
				}
			},
	}),
	lifecycle({
		componentDidMount() {
			on('click', this.props.handlePageClick);
		},

		componentWillUnmount() {
			off('click', this.props.handlePageClick);
		},
	}),
);

export function Datepicker({
	allValid,
	calendarShowing,
	closures,
	error,
	getRef,
	handleDateClick,
	helpText,
	id,
	label,
	name,
	onShowCalendar,
	position,
	placeholder,
	reservationWindow,
	today,
	withIcon,
	value,
}) {
	return (
		<div
			ref={getRef}
			className={classnames('ss-datepicker', position, {
				'with-icon': withIcon,
			})}
		>
			<Input
				name={name}
				id={id}
				helpText={helpText}
				label={label}
				value={value}
				onFocus={onShowCalendar}
				placeholder={placeholder}
				error={error}
				readOnly
			/>
			<CSSTransition
				classNames="transition-fade-in"
				in={calendarShowing}
				unmountOnExit
				timeout={150}
			>
				<Calendar
					closures={closures}
					reservationWindow={reservationWindow}
					today={today}
					selectedDate={value}
					onDateClick={handleDateClick}
					allValid={allValid}
				/>
			</CSSTransition>
		</div>
	);
}

Datepicker.propTypes = {
	allValid: PropTypes.bool,
	calendarShowing: PropTypes.bool,
	closures: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
	error: PropTypes.string,
	getRef: PropTypes.func.isRequired,
	helpText: PropTypes.string,
	id: PropTypes.string,
	label: PropTypes.string,
	name: PropTypes.string.isRequired,
	handleDateClick: PropTypes.func.isRequired,
	onShowCalendar: PropTypes.func,
	placeholder: PropTypes.string,
	position: PropTypes.string,
	reservationWindow: PropTypes.instanceOf(Date),
	today: PropTypes.instanceOf(Date),
	value: PropTypes.string.isRequired,
	withIcon: PropTypes.bool,
};

export default enhance(Datepicker);
